import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/Loader/Loader";
import authService from "../../services/auth.service";
import { LoginCredentials } from "../../types";
import "./Login.scss";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<LoginCredentials>({
    username: "",
    password: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [checkingSession, setCheckingSession] = useState<boolean>(true);
  const [redirectingToSso, setRedirectingToSso] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [redirectUrl, setRedirectUrl] = useState<string>("");

  const checkExistingSession = useCallback(async (currentRedirectUrl: string) => {
    try {
      setCheckingSession(true);
      const response = await authService.validateAdmin();

      if (response.valid && response.user) {
        authService.saveUser(response.user);
        if (currentRedirectUrl) {
          window.location.href = currentRedirectUrl;
        } else {
          navigate("/console");
        }
      }
    } catch {
      authService.clearAllData();
    } finally {
      setCheckingSession(false);
    }
  }, [navigate]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirect = urlParams.get("redirect");
    const isLogout = urlParams.get("logout") === "true";

    if (isLogout) {
      authService.clearAllData();
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (redirect) {
      setRedirectUrl(redirect);
    }

    void checkExistingSession(redirect || "");
  }, [checkExistingSession]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await authService.loginAdmin(credentials, redirectUrl);

      if (response.success && response.user) {
        authService.saveUser(response.user);
        if (response.redirectUrl || redirectUrl) {
          window.location.href = response.redirectUrl || redirectUrl;
        } else {
          navigate("/console");
        }
      }
    } catch (requestError: any) {
      setError(
        requestError.response?.data?.message ||
          "Login failed. Please check your credentials.",
      );
      authService.clearUser();
    } finally {
      setLoading(false);
    }
  };

  const handleSsoLogin = () => {
    setRedirectingToSso(true);
    window.location.href = authService.getAuthorizationUrl();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  if (checkingSession) {
    return <Loader message="Checking admin authentication..." />;
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-brand">
          <span className="login-brand__mark" aria-hidden="true">SAT</span>
          <div className="login-brand__text">
            <span className="login-brand__wordmark">
              SATashkent<span className="login-brand__sso">ADMIN</span>
            </span>
            <span className="login-brand__tagline">You Will Never Walk Alone</span>
          </div>
        </div>

        <div
          className="role-badge role-badge--admin"
          role="status"
          aria-label="Admin console"
        >
          <span className="role-badge__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
          </span>
          Admin Console
        </div>

        {redirectUrl && (
          <div className="login-header">
            <div className="redirect-info">
              <span>Redirecting to:</span>
              <strong>{new URL(redirectUrl).hostname}</strong>
            </div>
          </div>
        )}

        <div className="sso-widget">
          <div className="sso-widget__badge">Satashkent SSO</div>
          <h2>Login with Satashkent</h2>
          <p>
            Continue through the central portal and come back here automatically.
          </p>
          <button
            type="button"
            className="sso-widget__button"
            onClick={handleSsoLogin}
            disabled={loading || redirectingToSso}
          >
            {redirectingToSso ? "Redirecting..." : "Login with Satashkent"}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Admin Login or Email</label>
            <input
              type="text"
              id="username"
              name="username"
              value={credentials.username}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder="Enter admin login or email"
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={credentials.password}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder="Enter password"
              autoComplete="current-password"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" disabled={loading} className="login-button">
            {loading ? "Signing in..." : "Sign In as Admin"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
